import React, { useEffect, useRef, useState } from 'react';
import { getDocumentDownload } from '../../utilities';
import {
    CONNECTION_STATUS_CONNECTED,
    CONNECTION_STATUS_FAILED_TO_CONNECT,
    CONVERSATION_NAME_DOCUMENT,
    CONVERSATION_TYPE_EVENT,
    CONVERSATION_TYPE_TRACE,
    DIRECT_LINE_CONNECTION_REGION_MISMATCH,
    DIRECT_LINE_EUROPE_DOMAIN,
    DIRECT_LINE_GLOBAL_DOMAIN,
    HTTPS,
    ILxsServiceAgentViewProps,
    INCOMING_ACTIVITY,
    InitWebChatParams,
    IStoredConversation,
    MAX_TRANSCRIPT_COUNT,
    SET_CONTEXT_EVENT,
    START_CONVERSATION_EVENT,
    START_CONVERSATION_RESET_EVENT
} from './lxs-service-agent.data';

const initWebChat = ({
    directLineToken,
    directLineDomain,
    savedConversation,
    userToken,
    context,
    resources,
    userName,
    locale,
    canonicalDomain,
    container,
    isReset,
    writeStoredConversation,
    onRegionalConnectionFailure
}: InitWebChatParams): void => {
    const WebChat = window.WebChat;
    if (!WebChat) {
        return;
    }

    const activityMiddleware = () => (next: any) => (card: any) => {
        const { activity } = card;
        const isDownloadActivity = activity?.type === CONVERSATION_TYPE_EVENT && activity?.name === CONVERSATION_NAME_DOCUMENT;
        const documentName = activity.value?.displayName ? activity.value.displayName : activity.value?.id;

        if (isDownloadActivity) {
            const downloadedRef: React.MutableRefObject<Set<string>> = {
                current: new Set<string>([])
            };
            return () => (
                <div className='webchat__bubble--nub-on-top webchat__bubble--document-download'>
                    <a
                        className='lxs-download-link webchat__bubble__content'
                        onClick={() => {
                            const documentId = activity.value?.id;
                            void getDocumentDownload(documentId, downloadedRef, context, resources);
                        }}
                    >
                        {documentName || resources.lxsServiceAgentDownloadDocumentDefaultName}
                    </a>
                </div>
            );
        }

        return next(card);
    };

    const directLine = WebChat.createDirectLine({
        token: directLineToken,
        conversationId: savedConversation?.conversationId,
        domain: directLineDomain
    });

    const isRestoring = Boolean(savedConversation);
    const transcript = savedConversation?.transcript.slice() || [];
    let isHydratingTranscript = isRestoring;

    const persistBlob = (updatedFields: Partial<IStoredConversation> = {}) => {
        const base: IStoredConversation = {
            conversationId: savedConversation?.conversationId || '',
            directLineToken: String(directLineToken),
            userToken,
            transcript
        };
        const blob = { ...base, ...updatedFields };
        writeStoredConversation(blob);
    };

    const store = WebChat.createStore({}, () => (next: (arg0: any) => any) => (action: { type: string; payload: { activity: any } }) => {
        if (action.type === INCOMING_ACTIVITY) {
            if (action.payload.activity?.type === CONVERSATION_TYPE_TRACE) {
                return;
            }
            if (!isHydratingTranscript) {
                transcript.push(action.payload.activity);
                if (transcript.length > MAX_TRANSCRIPT_COUNT) {
                    transcript.splice(0, transcript.length - MAX_TRANSCRIPT_COUNT);
                }
                persistBlob();
            }
        }
        return next(action);
    });

    if (isRestoring && savedConversation) {
        savedConversation.transcript.forEach(activity => store.dispatch({ type: INCOMING_ACTIVITY, payload: { activity } }));
        isHydratingTranscript = false;
    }

    const connSub = directLine.connectionStatus$.subscribe((status: number) => {
        if (status === CONNECTION_STATUS_CONNECTED) {
            const convId = directLine.conversationId!;
            savedConversation = {
                conversationId: convId,
                directLineToken: String(directLineToken),
                userToken,
                transcript
            };
            persistBlob({ conversationId: convId });

            directLine
                .postActivity({
                    from: { id: userName },
                    type: CONVERSATION_TYPE_EVENT,
                    name: SET_CONTEXT_EVENT,
                    value: { jwtLoopNeu: userToken, name: userName, BaseUrlLoop: HTTPS + canonicalDomain, locale }
                })
                .subscribe(
                    () => {
                        if (!isRestoring) {
                            directLine
                                .postActivity({
                                    from: { id: userName },
                                    type: CONVERSATION_TYPE_EVENT,
                                    name: isReset ? START_CONVERSATION_RESET_EVENT : START_CONVERSATION_EVENT
                                })
                                .subscribe({ error: (err: any) => console.error(err) });
                        }
                    },
                    (err: any) => console.error(err)
                );

            connSub.unsubscribe();
            return;
        }

        if (status === CONNECTION_STATUS_FAILED_TO_CONNECT) {
            connSub.unsubscribe();
            if (directLineDomain === DIRECT_LINE_EUROPE_DOMAIN && onRegionalConnectionFailure) {
                void onRegionalConnectionFailure();
            }
        }
    });

    WebChat.renderWebChat(
        {
            directLine,
            userID: userName,
            locale,
            store,
            styleOptions: { hideUploadButton: true },
            activityMiddleware
        },
        container
    );
};

const LxsServiceAgentView = (props: ILxsServiceAgentViewProps) => {
    const {
        resources,
        CLASS_NAME,
        validateRegionalTokenError,
        probeRegionalDirectLine,
        fetchDirectLineToken,
        readStoredConversation,
        writeStoredConversation,
        clearStoredConversation,
        getValidStoredConversation
    } = props;

    const { requestContext } = props.context.actionContext;
    const { isAuthenticated } = requestContext?.user;
    const currentUserToken = props.context.request.user.token;
    const { canonicalDomain } = props.context.request as { canonicalDomain: string };

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [dlToken, setDlToken] = useState<string | null>(null);
    const [restartConversationCount, setRestartConversationCount] = useState<number>(0);
    const [directLineDomain, setDirectLineDomain] = useState<string>(DIRECT_LINE_EUROPE_DOMAIN);

    const webchatRef = useRef<HTMLDivElement | null>(null);
    const isResetRef = useRef<boolean>(false);

    const _handleRegionalConnectionFailure = async (tokenToValidate: string): Promise<void> => {
        const hasTokenError = await validateRegionalTokenError(tokenToValidate);
        if (hasTokenError) {
            return;
        }

        setDirectLineDomain(currentDomain => (currentDomain === DIRECT_LINE_EUROPE_DOMAIN ? DIRECT_LINE_GLOBAL_DOMAIN : currentDomain));
    };

    // Effect: fetch Direct Line token (and refresh on restart)
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        let cancelled: boolean = false;

        const load = async () => {
            const savedConversation = readStoredConversation();

            let directLineToken: string | null = null;

            // On restart, or first load, we may want to fetch a new token or reuse
            if (!dlToken || restartConversationCount > 0) {
                const isRestart = isResetRef.current;
                if (!isRestart && savedConversation?.userToken === currentUserToken) {
                    directLineToken = savedConversation.directLineToken;
                } else {
                    // force a fresh token on restart
                    directLineToken = await fetchDirectLineToken(props.context);
                }
            } else {
                directLineToken = dlToken;
            }

            if (cancelled) {
                return;
            }

            if (directLineToken) {
                setDlToken(prev => (prev !== directLineToken ? directLineToken : prev));
            }
        };

        void load();

        return () => {
            cancelled = true;
        };
    }, [
        props.context.actionContext,
        restartConversationCount,
        fetchDirectLineToken,
        currentUserToken,
        clearStoredConversation,
        readStoredConversation
    ]);

    // Effect: init WebChat when dlToken changes (including after restart)
    useEffect(() => {
        if (typeof window === 'undefined' || !dlToken || !webchatRef.current) {
            return;
        }

        let cancelled = false;

        const initializeWebChat = async () => {
            const directLineToken = dlToken;
            const userTokenForConversation = props.context.request.user.token;

            // Preflight europe endpoint before rendering chat to avoid "Unable to connect"
            if (directLineDomain === DIRECT_LINE_EUROPE_DOMAIN) {
                const probeResult = await probeRegionalDirectLine(directLineToken);
                if (cancelled) {
                    return;
                }

                if (probeResult === DIRECT_LINE_CONNECTION_REGION_MISMATCH) {
                    setDirectLineDomain(currentDomain =>
                        currentDomain === DIRECT_LINE_EUROPE_DOMAIN ? DIRECT_LINE_GLOBAL_DOMAIN : currentDomain
                    );
                    return;
                }
            }

            const savedConversation = getValidStoredConversation(directLineToken, userTokenForConversation);

            // on every dlToken change (including restart), re-init WebChat
            // hint: local development with mockUser causes re-inits on every page load due to token changes
            const isReset = isResetRef.current;
            isResetRef.current = false;

            if (!webchatRef.current) {
                return;
            }

            initWebChat({
                directLineToken,
                directLineDomain,
                savedConversation,
                userToken: userTokenForConversation,
                context: props.context,
                resources: props.resources,
                userName: props.context.request.user.firstName,
                locale: props.context.request.locale,
                canonicalDomain,
                container: webchatRef.current,
                isReset,
                writeStoredConversation,
                onRegionalConnectionFailure: async () => _handleRegionalConnectionFailure(directLineToken)
            });
        };

        void initializeWebChat();

        return () => {
            cancelled = true;
        };
    }, [
        dlToken,
        directLineDomain,
        canonicalDomain,
        props.context.request.user.token,
        props.context.request.user.firstName,
        props.context.request.locale,
        probeRegionalDirectLine,
        getValidStoredConversation,
        writeStoredConversation
    ]);

    const handleRestart = () => {
        clearStoredConversation();
        isResetRef.current = true;
        setDirectLineDomain(DIRECT_LINE_EUROPE_DOMAIN);
        setDlToken(null);
        setRestartConversationCount((count: number) => count + 1);
    };

    if (!isAuthenticated) {
        return null;
    }

    return (
        <>
            <div className={CLASS_NAME}>
                <button
                    className={`${CLASS_NAME}__open-button${isOpen ? ` ${CLASS_NAME}__open-button--active` : ''}`}
                    onClick={() => setIsOpen(open => !open)}
                >
                    {resources.lxsServiceAgentName}
                </button>
            </div>
            <div
                className={`${CLASS_NAME}__frame${isOpen ? ` ${CLASS_NAME}__frame--open` : ` ${CLASS_NAME}__frame--closed`}${
                    isExpanded ? ` ${CLASS_NAME}__frame--expanded` : ''
                }`}
            >
                <div className={`${CLASS_NAME}__frame-heading`}>
                    <button
                        className={`${CLASS_NAME}__frame-${isExpanded ? 'minimize' : 'expand'}`}
                        title={resources.lxsServiceAgentExpandChatTitle}
                        onClick={() => setIsExpanded((expanded: boolean) => !expanded)}
                    />
                    <h5 className={`${CLASS_NAME}__frame-heading-text`}>{resources.lxsServiceAgentTitle}</h5>
                    <button
                        className={`${CLASS_NAME}__reload`}
                        onClick={handleRestart}
                        title={resources.lxsServiceAgentRestartConversationTitle}
                    />
                </div>
                <div id={CLASS_NAME} ref={webchatRef} />
            </div>
        </>
    );
};

export default LxsServiceAgentView;
